import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Fade,
  IconButton,
  Typography,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CommentIcon from "@mui/icons-material/Comment";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { API_URL } from "../../consts";
import { useDispatch, useSelector } from "react-redux";
import RateCard from "./Comments/RateCard";
import ConfirmationDialog from "../common/ConfirmationDialog";

const CommentsDialog = ({ open, onClose, rateId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedComments, setExpandedComments] = useState({});
  const [newComments, setNewComments] = useState({});
  const [submitting, setSubmitting] = useState({});
  const [comments, setComments] = useState({});
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState(null);
  const [rateSpaceOwnership, setRateSpaceOwnership] = useState({}); // Map of rate_id -> isSpaceOwner
  const dispatch = useDispatch();
  const authorized = useSelector((state) => state.authorized);
  const user = useSelector((state) => state.user);

  useEffect(() => {
    if (open && rateId) {
      getComments();
    }
  }, [open, rateId]);

  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  const showNotification = (message, severity = 'success') => {
    setNotification({ open: true, message, severity });
  };

  const getComments = async () => {
    setLoading(true);
    try {
      const url = `${API_URL}/rates/${rateId}/marks?start_from=0&end_record=50`;
      const response = await axios.get(url);
      setRates(response.data);
      
      // Initialize expanded state for each rate - first rate expanded by default
      const initialExpandedState = {};
      response.data.forEach((rate, index) => {
        initialExpandedState[rate.id] = index === 0; // First rate expanded by default
      });
      setExpandedComments(initialExpandedState);
      
      // Initialize new comments state for each rate
      const initialCommentsState = {};
      response.data.forEach(rate => {
        initialCommentsState[rate.id] = "";
      });
      setNewComments(initialCommentsState);
      
      // Fetch space ownership information for each rate
      if (authorized && user) {
        const ownershipMap = {};
        const currentUserName = user?.user_name || user?.username || user?.name;
        
        // Fetch rating details for each rate to get space information
        const ownershipPromises = response.data.map(async (rate) => {
          try {
            const ratingResponse = await axios.get(`${API_URL}/rates/ratings/${rate.id}`);
            const ratingData = ratingResponse.data;
            const spacesList = ratingData.spaces_list || [];
            
            // If rating has spaces, check if user is owner of any space
            if (spacesList.length > 0) {
              // Fetch space details to check ownership
              const spaceOwnershipPromises = spacesList.map(async (space) => {
                try {
                  // Try authenticated endpoint first, then public
                  let spaceResponse;
                  try {
                    spaceResponse = await axios.get(`${API_URL}/spaces/authenticated/spaces/${space.id}`);
                  } catch (err) {
                    spaceResponse = await axios.get(`${API_URL}/spaces/${space.id}`);
                  }
                  
                  const spaceData = spaceResponse.data?.space || spaceResponse.data;
                  const spaceOwnerName = spaceData.user_name || spaceData.username;
                  return spaceOwnerName && currentUserName && (spaceOwnerName === currentUserName);
                } catch (err) {
                  return false;
                }
              });
              
              const ownershipResults = await Promise.all(spaceOwnershipPromises);
              ownershipMap[rate.id] = ownershipResults.some(isOwner => isOwner);
            } else {
              ownershipMap[rate.id] = false;
            }
          } catch (err) {
            // If we can't fetch rating details, assume not space owner
            ownershipMap[rate.id] = false;
          }
        });
        
        await Promise.all(ownershipPromises);
        setRateSpaceOwnership(ownershipMap);
      }
      
      // Automatically fetch comments for the first rate if it exists
      if (response.data.length > 0) {
        await fetchRateComments(response.data[0]);
      }
    } catch (err) {
      if (err?.response?.status === 401) {
        dispatch({ type: "SET_UNAUTHORIZED" });
        return;
      }
      showNotification(err?.response?.data?.error || "Failed to load comments", 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchRateComments = async (rate, parentCommentId = null) => {
    try {
      const url = `${API_URL}/comments/${rate.id}${parentCommentId ? `?comment_id=${parentCommentId}` : ''}`;
      const response = await axios.get(url);
      if (response.status === 200) {
        setComments(prev => ({
          ...prev,
          [rate.id]: response.data.comments || []
        }));
      }
    } catch (err) {
      if (err?.response?.status === 401) {
        dispatch({ type: "SET_UNAUTHORIZED" });
        return;
      }
      showNotification(err?.response?.data?.error || "Failed to load comments", 'error');
    }
  };

  const handleSubmitComment = async (rate, parentCommentId = null, replyText = null) => {
    const commentText = replyText || newComments[rate.id];
    if (!commentText?.trim() || !authorized) {
      if (!authorized) {
        showNotification("You must be logged in to comment", 'error');
      }
      return;
    }

    setSubmitting(prev => ({ ...prev, [rate.id]: true }));
    try {
      const response = await axios.post(`${API_URL}/comments`, {
        rating_id: rate.id,
        rated_object_id: rate.id,
        comment: commentText,
        comment_id: parentCommentId
      });

      if (response.status === 200) {
        // If it's a reply, update the parent comment's replies
        if (parentCommentId) {
          setComments(prev => ({
            ...prev,
            [rate.id]: prev[rate.id].map(comment => {
              if (comment.id === parentCommentId) {
                return {
                  ...comment,
                  replies: [...(comment.replies || []), response.data]
                };
              }
              return comment;
            })
          }));
        } else {
          // For top-level comments, refresh the entire list
          await fetchRateComments(rate);
        }
        
        // Clear the input
        if (parentCommentId) {
          // If it's a reply, we don't need to clear the main comment input
          setNewComments(prev => ({ ...prev, [rate.id]: newComments[rate.id] }));
        } else {
          setNewComments(prev => ({ ...prev, [rate.id]: "" }));
        }
        
        showNotification("Comment added successfully");
      }
    } catch (err) {
      if (err?.response?.status === 401) {
        dispatch({ type: "SET_UNAUTHORIZED" });
        return;
      }
      showNotification(err?.response?.data?.error || "Failed to add comment", 'error');
    } finally {
      setSubmitting(prev => ({ ...prev, [rate.id]: false }));
    }
  };

  const toggleComments = async (rate, parentCommentId = null) => {
    if (parentCommentId) {
      // This is a request to fetch replies for a comment
      try {
        // Always fetch replies when clicking on a comment
        const response = await axios.get(`${API_URL}/comments/${rate.id}?comment_id=${parentCommentId}`);
        if (response.status === 200) {
          setComments(prev => ({
            ...prev,
            [rate.id]: prev[rate.id].map(comment => 
              comment.id === parentCommentId 
                ? { ...comment, replies: response.data.comments || [] }
                : comment
            )
          }));
        }
      } catch (err) {
        if (err?.response?.status === 401) {
          dispatch({ type: "SET_UNAUTHORIZED" });
          return;
        }
        showNotification(err?.response?.data?.error || "Failed to load replies", 'error');
      }
    } else {
      // This is a request to fetch top-level comments
      const newExpandedState = !expandedComments[rate.id];
      setExpandedComments(prev => ({
        ...prev,
        [rate.id]: newExpandedState
      }));

      if (newExpandedState) {
        // Always fetch comments when expanding
        await fetchRateComments(rate);
      } else {
        // When collapsing comments, clear all replies
        setComments(prev => ({
          ...prev,
          [rate.id]: prev[rate.id]?.map(comment => ({
            ...comment,
            replies: [] // Clear all replies when collapsing
          })) || []
        }));
      }
    }
  };

  const handleCommentChange = (rateId, value) => {
    setNewComments(prev => ({
      ...prev,
      [rateId]: value
    }));
  };

  const handleDeleteComment = (commentId) => {
    if (!commentId || !authorized) {
      showNotification("You must be logged in to delete comments", 'error');
      return;
    }

    // Open confirmation dialog
    setCommentToDelete(commentId);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteComment = async () => {
    if (!commentToDelete) return;

    try {
      const response = await axios.delete(`${API_URL}/comments/${commentToDelete}`);
      
      if (response.status === 200) {
        // Remove the comment from the UI
        // We need to update the comments state for all rates
        setComments(prev => {
          const updated = { ...prev };
          for (const rateId in updated) {
            // Remove comment from top-level comments
            updated[rateId] = updated[rateId].filter(c => c.id !== commentToDelete);
            // Also remove from replies
            updated[rateId] = updated[rateId].map(c => {
              if (c.replies) {
                c.replies = c.replies.filter(r => r.id !== commentToDelete);
              }
              return c;
            });
          }
          return updated;
        });
        
        showNotification("Comment deleted successfully");
        setDeleteDialogOpen(false);
        setCommentToDelete(null);
      }
    } catch (err) {
      if (err?.response?.status === 401) {
        dispatch({ type: "SET_UNAUTHORIZED" });
        return;
      }
      showNotification(err?.response?.data?.error || "Failed to delete comment", 'error');
      setDeleteDialogOpen(false);
      setCommentToDelete(null);
    }
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="sm" 
        fullWidth
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 300 }}
        PaperProps={{
          sx: {
            width: { xs: "100vw", sm: "90vw", md: "600px" },
            height: { xs: "100vh", sm: "90vh" },
            maxHeight: { xs: "100vh", sm: "90vh" },
            margin: { xs: 0, sm: "auto" },
            borderRadius: { xs: 0, sm: 3 },
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
            background: "linear-gradient(145deg, #ffffff, #f5f5f5)",
          },
        }}
        sx={{
          "& .MuiDialog-container": {
            alignItems: isMobile ? "flex-start" : "center",
            background: "rgba(0, 0, 0, 0.5)",
            padding: { xs: 0, sm: 2 },
          },
        }}
      >
        <DialogTitle sx={{ 
          m: 0, 
          p: { xs: 1.5, sm: 2 }, 
          display: 'flex', 
          alignItems: 'center', 
          gap: { xs: 0.75, sm: 1 },
          color: 'primary.main',
          fontWeight: 600,
          fontSize: { xs: "1.125rem", sm: "1.25rem" },
        }}>
          <CommentIcon sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }} />
          Reviews
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: { xs: 4, sm: 8 },
              top: { xs: 4, sm: 8 },
              color: (theme) => theme.palette.grey[500],
              minWidth: { xs: "44px", sm: "40px" },
              minHeight: { xs: "44px", sm: "40px" },
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
              },
              '&:active': {
                backgroundColor: 'rgba(0, 0, 0, 0.1)',
              },
            }}
          >
            <CloseIcon fontSize={isMobile ? "medium" : "small"} />
          </IconButton>
        </DialogTitle>
        <DialogContent 
          dividers 
          sx={{ 
            p: { xs: 1.5, sm: 2, md: 3 },
            overflow: "auto",
            maxHeight: { xs: "calc(100vh - 160px)", sm: "calc(90vh - 160px)" },
            "&::-webkit-scrollbar": {
              width: { xs: "6px", sm: "8px" },
            },
            "&::-webkit-scrollbar-track": {
              background: "#f1f1f1",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#888",
              borderRadius: "4px",
              "&:hover": {
                background: "#555",
              },
            },
          }}
        >
          {loading ? (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              py: { xs: 3, sm: 4 },
            }}>
              <Typography sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
                Loading comments...
              </Typography>
            </Box>
          ) : rates.length === 0 ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              py: { xs: 3, sm: 4 },
              color: 'text.secondary',
            }}>
              <CommentIcon sx={{ fontSize: { xs: 36, sm: 48 }, mb: { xs: 1.5, sm: 2 }, opacity: 0.5 }} />
              <Typography 
                variant="body1" 
                sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
              >
                No comments available yet.
              </Typography>
            </Box>
          ) : (
            rates.map((rate) => (
              <RateCard
                key={rate.id}
                rate={rate}
                expandedComments={expandedComments}
                comments={comments}
                newComments={newComments}
                submitting={submitting}
                authorized={authorized}
                onToggleComments={toggleComments}
                onCommentChange={handleCommentChange}
                onSubmitComment={handleSubmitComment}
                onDeleteComment={handleDeleteComment}
                currentUser={user}
                isSpaceOwner={rateSpaceOwnership[rate.id] || false}
              />
            ))
          )}
        </DialogContent>
        <DialogActions sx={{ 
          p: { xs: 1.5, sm: 2 },
          borderTop: "1px solid rgba(0, 0, 0, 0.05)",
        }}>
          <Button 
            variant="contained" 
            onClick={onClose}
            fullWidth={isMobile}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              px: { xs: 2, sm: 3 },
              py: { xs: 1.25, sm: 1 },
              fontSize: { xs: "0.875rem", sm: "0.9375rem" },
              minHeight: { xs: "44px", sm: "auto" },
              "&:hover": {
                backgroundColor: "primary.dark",
              },
              "&:active": {
                backgroundColor: "primary.dark",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={3000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setCommentToDelete(null);
        }}
        onConfirm={confirmDeleteComment}
        title={"Delete comment"}
        cancelButtonString={"Cancel"}
        confirmButtonString={"Delete"}
        message={"Are you sure you want to delete this comment?"}
      />
    </>
  );
};

export default CommentsDialog;
